/**
 * PlanSelectionOnboarding.jsx
 *
 * Full-page, distraction-free plan selection screen shown immediately after
 * a merchant installs Tax & Duty King for the first time.
 *
 * Visibility is controlled server-side via the `plan_selected` flag on the
 * store document. The parent (App.js) checks /api/setup/status on load;
 * if planSelected === false, it renders this component.
 *
 * When the merchant clicks "Start Free Trial":
 *  1. POST /api/setup/plan-selected  → marks flag true (prevents re-showing)
 *  2. POST /api/billing/create       → gets Shopify confirmation URL
 *  3. Redirect to Shopify billing confirmation page
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { redirectToShopifyBilling } from '../utils/billingRedirect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_API_KEY;
const ENABLE_MANAGED_PRICING = process.env.REACT_APP_ENABLE_MANAGED_PRICING === 'true';

const api = axios.create({
  baseURL: BACKEND_URL + '/api',
  headers: {
    'x-api-key': API_KEY,
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// Plans shown on the onboarding screen (excludes trial default and plus which is sales-assisted)
const DISPLAY_PLAN_HANDLES = ['starter', 'growth', 'enterprise'];

const PlanSelectionOnboarding = ({ shopDomain }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selecting, setSelecting] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      let plansData = [];
      try {
        const res = await api.get('/subscription/plans');
        if (res.data?.success && res.data?.plans) {
          plansData = res.data.plans;
        }
      } catch (err) {
        console.warn('PlanSelectionOnboarding: plans fetch failed', err);
      }
      // Filter and order by DISPLAY_PLAN_HANDLES
      const ordered = DISPLAY_PLAN_HANDLES
        .map(h => plansData.find(p => p.handle === h))
        .filter(Boolean);
      setPlans(ordered);
    } catch (err) {
      setError('Unable to load plans. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (planHandle) => {
    if (selecting) return;
    setSelecting(planHandle);
    setError(null);

    const shop = shopDomain || getShopFromUrl();

    try {
      // Step 1: Redirect to billing.
      // NOTE: We do NOT mark plan_selected=True here.
      // setup.py now derives planSelected from the actual Shopify billing status via GraphQL.
      // If the merchant dismisses the billing page without subscribing, the next load
      // will find no active subscription and show the plan picker again.
      // When ENABLE_MANAGED_PRICING=true the backend returns 410 for /billing/create.
      // In that case we redirect directly to the Shopify hosted pricing page.
      if (ENABLE_MANAGED_PRICING) {
        console.log('[TDK] Managed pricing enabled — redirecting to Shopify hosted pricing page');
        // Return to the embedded launch guide after Shopify confirms billing.
        try { localStorage.setItem('tdk_post_billing_tab', 'setup'); } catch(e) {}
        if (!redirectToShopifyBilling(shop)) {
          setError('Unable to open Shopify billing. Please contact support to verify the app billing configuration.');
          setSelecting(null);
        }
        return;
      }

      // Legacy path: call /billing/create and redirect to confirmation URL
      const correlationId = `onboarding_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const urlParams = new URLSearchParams(window.location.search);
      const hostParam = urlParams.get('host');

      const payload = {
        shop_domain: shop,
        plan_handle: planHandle,
        billing_period: billingPeriod,
        correlation_id: correlationId,
      };
      if (hostParam) payload.host = hostParam;

      const response = await api.post('/billing/create', payload);

      if (response.data?.success && response.data?.confirmation_url) {
        const confirmationUrl = response.data.confirmation_url;
        // Return to the embedded launch guide after Shopify confirms billing.
        try { localStorage.setItem('tdk_post_billing_tab', 'setup'); } catch(e) {}
        try {
          window.top.location.href = confirmationUrl;
        } catch (e) {
          window.location.href = confirmationUrl;
        }
      } else {
        const msg = response.data?.message || response.data?.error || 'Failed to create subscription. Please try again.';
        setError(msg);
        setSelecting(null);
      }
    } catch (err) {
      console.error('PlanSelectionOnboarding: billing error', err);
      // If the backend returns 410 (managed pricing), fall back to hosted pricing
      if (err.response?.status === 410) {
        console.log('[TDK] Got 410 — falling back to Shopify hosted pricing page');
        try { localStorage.setItem('tdk_post_billing_tab', 'setup'); } catch(e) {}
        if (!redirectToShopifyBilling(shop)) {
          setError('Unable to open Shopify billing. Please contact support to verify the app billing configuration.');
          setSelecting(null);
        }
        return;
      }
      const msg = err.response?.data?.message || err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
      setSelecting(null);
    }
  };

  const getShopFromUrl = () => {
    const p = new URLSearchParams(window.location.search);
    return p.get('shop') || '';
  };

  const formatPrice = (amount) => {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getEffectiveMonthly = (plan) => {
    if (billingPeriod === 'yearly' && plan.yearly_price) {
      return Math.round((plan.yearly_price / 12) * 100) / 100;
    }
    return plan.monthly_price;
  };

  const getYearlySaving = (plan) => {
    if (!plan.monthly_price || !plan.yearly_price) return 0;
    return Math.round(
      ((plan.monthly_price * 12 - plan.yearly_price) / (plan.monthly_price * 12)) * 1000
    ) / 10;
  };

  return (
    <div className="plan-onboarding-overlay">
      <div className="plan-onboarding-container">

        {/* Header */}
        <div className="plan-onboarding-header">
          <div className="plan-onboarding-logo">
            <span className="plan-onboarding-crown">👑</span>
            <span className="plan-onboarding-brand">Tax &amp; Duty King</span>
          </div>
          <h1 className="plan-onboarding-title">
            Choose your plan to start your free trial
          </h1>
          <p className="plan-onboarding-subtitle">
            No charge today. Your 7-day free trial starts the moment you select a plan.
          </p>
        </div>

        {/* Billing period toggle */}
        <div className="plan-onboarding-toggle">
          <button
            className={`plan-onboarding-toggle-btn${billingPeriod === 'monthly' ? ' active' : ''}`}
            onClick={() => setBillingPeriod('monthly')}
          >
            Monthly
          </button>
          <button
            className={`plan-onboarding-toggle-btn${billingPeriod === 'yearly' ? ' active' : ''}`}
            onClick={() => setBillingPeriod('yearly')}
          >
            Yearly
            <span className="plan-onboarding-save-badge">Save up to 17%</span>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="plan-onboarding-error" role="alert">
            {error}
          </div>
        )}

        {/* Plan cards */}
        {loading ? (
          <div className="plan-onboarding-loading">Loading plans…</div>
        ) : (
          <div className="plan-onboarding-grid">
            {plans.map((plan) => {
              const isPopular = plan.handle === 'growth';
              const isSelecting = selecting === plan.handle;
              const price = billingPeriod === 'yearly' ? plan.yearly_price : plan.monthly_price;
              const period = billingPeriod === 'yearly' ? '/year' : '/month';
              const effectiveMonthly = getEffectiveMonthly(plan);
              const saving = getYearlySaving(plan);

              return (
                <div
                  key={plan.handle}
                  className={`plan-onboarding-card${isPopular ? ' popular' : ''}${isSelecting ? ' selecting' : ''}`}
                >
                  {isPopular && (
                    <div className="plan-onboarding-popular-badge">★ Most Popular</div>
                  )}

                  <div className="plan-onboarding-card-header">
                    <h2 className="plan-onboarding-plan-name">{plan.name}</h2>
                    <div className="plan-onboarding-price">
                      <span className="plan-onboarding-price-amount">{formatPrice(price)}</span>
                      <span className="plan-onboarding-price-period">{period}</span>
                    </div>
                    {billingPeriod === 'yearly' && saving > 0 && (
                      <div className="plan-onboarding-effective-monthly">
                        {formatPrice(effectiveMonthly)}/month effective · Save {saving}%
                      </div>
                    )}
                  </div>

                  <div className="plan-onboarding-card-body">
                    <div className="plan-onboarding-calc-limit">
                      <strong>{plan.calculation_limit?.toLocaleString()}</strong> calculations/month
                    </div>
                    <ul className="plan-onboarding-features">
                      {(plan.features || []).map((feature, i) => (
                        <li key={i}>
                          <span className="plan-onboarding-check">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="plan-onboarding-card-footer">
                    <div className="plan-onboarding-trial-note">
                      🎁 {plan.trial_days || 7}-day free trial included
                    </div>
                    <button
                      className={`plan-onboarding-cta${isPopular ? ' popular-cta' : ''}`}
                      onClick={() => handleSelect(plan.handle)}
                      disabled={!!selecting}
                      aria-busy={isSelecting}
                    >
                      {isSelecting ? 'Redirecting to Shopify…' : 'Start Free Trial'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Plus plan note */}
        <div className="plan-onboarding-plus-note">
          Need more than 10,000 calculations/month?{' '}
          <a href="mailto:contact@aspenconsulting.co.uk">Contact us</a> about the Plus plan
          ($299/mo · 20,000 calculations).
        </div>

        {/* Trust signals */}
        <div className="plan-onboarding-trust">
          <span><span className="trust-check">✓</span> No credit card charged today</span>
          <span><span className="trust-check">✓</span> Cancel any time</span>
          <span><span className="trust-check">✓</span> Billed securely via Shopify</span>
          <span><span className="trust-check">✓</span> All plans include 7-day free trial</span>
        </div>

      </div>
    </div>
  );
};

export default PlanSelectionOnboarding;
